import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import ButtonIcon from "Components/buttonIcon/ButtonIcon";
import { arrowRight, arrowLeft } from "Icons/index";
import cx from "classnames";
import qs from "query-string";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";

/**
 * Pager component for pagination controls
 * @param {Object} props - Component props
 * @param {Object} props.paginationInfo - Pagination information
 * @param {string} props.url - Base URL for pagination
 * @param {number} [props.shortenPagesOver] - Number of pages after which to shorten display
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.loading] - Loading state
 */
const Pager = ({ paginationInfo, url, shortenPagesOver, className, loading }) => {
  const history = useHistory();
  const location = useLocation();
  const params = qs.parse(location.search);

  const [page, setPage] = useState(() => {
    const fromQuery = Number(params.page);
    return !isNaN(fromQuery) && fromQuery > 0 ? fromQuery : 1;
  });

  useEffect(() => {
    const fromQuery = Number(params.page);
    setPage(!isNaN(fromQuery) && fromQuery > 0 ? fromQuery : 1);
  }, [params.page]);

  const goTo = (targetPage) => {
    const { per, query, filter, status, sort, sort_field, customer_code } = paginationInfo || {};

    history.push({
      pathname: url,
      search: qs.stringify({
        ...params,
        page: targetPage,
        // Only include pagination params if they exist
        per: per || undefined,
        query: query || undefined,
        filter: filter || undefined,
        status: status || undefined,
        sort: sort || undefined,
        sort_field: sort_field || undefined,
        customer_code: customer_code || undefined,
      }),
      state: { prevPathname: url },
    });
  };

  /**
   * Handle click on previous page button
   */
  const onClickPrev = () => {
    if (page <= 1) return;
    const target = page - 1;
    setPage(target);
    goTo(target);
  };

  /**
   * Handle click on next page button
   */
  const onClickNext = () => {
    if (page >= paginationInfo.total_pages) return;
    const target = page + 1;
    setPage(target);
    goTo(target);
  };
  /**
   * Shorten page display if over specified limit
   * @returns {string|number} Shortened or full page count
   */
  const doShortenPagesOver = () => {
    if (shortenPagesOver && shortenPagesOver < (paginationInfo.total_pages || 0)) {
      return `${shortenPagesOver}+`;
    }
    return paginationInfo.total_pages;
  };

  const totalPages = paginationInfo?.total_pages || 0;
  const noPages = totalPages === 0;

  const isButtonNextDisabled = page === totalPages || noPages;
  const isButtonPrevDisabled = page <= 1 || noPages;

  return (
    <div className={cx(className, "grid grid-cols-[2rem_fr_2rem] items-center")}>
      <ButtonIcon
        className={cx("col-start-1", {
          "!opacity-20 pointer-events-none": loading || isButtonPrevDisabled,
        })}
        icon={arrowLeft}
        onClick={onClickPrev}
        disabled={isButtonPrevDisabled}
      />

      <div
        className={cx("col-start-2 text-center text-sm font-semibold text-text select-none", {
          "opacity-30 pointer-events-none": noPages,
        })}
      >
        {noPages ? (
          "-"
        ) : (
          <>
            {page}
            <span className="font-normal opacity-50 before:content-['/'] before:mx-1">{doShortenPagesOver()}</span>
          </>
        )}
      </div>

      <ButtonIcon
        className={cx("col-start-3", {
          "!opacity-20 pointer-events-none": loading || isButtonNextDisabled,
        })}
        icon={arrowRight}
        onClick={onClickNext}
        disabled={isButtonNextDisabled}
      />
    </div>
  );
};

Pager.propTypes = {
  paginationInfo: PropTypes.object,
  url: PropTypes.string.isRequired,
  shortenPagesOver: PropTypes.number,
  className: PropTypes.string,
  loading: PropTypes.bool,
};

export default Pager;
